import axios from 'axios'
import React, { useEffect, useState } from 'react'

const cardStyle = {
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '16px',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)'
}

const formatNumber = value => Number(value || 0).toLocaleString('en-US')

const formatDate = value => {
  if (!value) return '--'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '--'
  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

const StatCard = ({ title, value, subtitle }) => (
  <div style={{ ...cardStyle, padding: '20px 22px', minHeight: '110px' }}>
    <p
      style={{
        margin: 0,
        color: '#64748b',
        fontSize: '13px',
        fontWeight: 600,
        letterSpacing: '0.02em'
      }}
    >
      {title}
    </p>
    <h3
      style={{
        margin: '12px 0 8px',
        color: '#0f172a',
        fontSize: '32px',
        fontWeight: 700
      }}
    >
      {value}
    </h3>
    {subtitle && (
      <p style={{ margin: 0, color: '#475569', fontSize: '12px' }}>
        {subtitle}
      </p>
    )}
  </div>
)

const MaxifyBannerActivity = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)
        setError('')
        const res = await axios.get('/api/activity/maxify-banner-click')
        setData(res?.data?.data || null)
      } catch (err) {
        setError(
          err?.response?.data?.error || 'Failed to load Maxify banner clicks'
        )
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  if (loading) {
    return (
      <div className='font18' style={{ padding: '24px', color: '#475569' }}>
        Loading Maxify banner activity...
      </div>
    )
  }

  if (error) {
    return (
      <div className='font18' style={{ padding: '24px' }}>
        <div
          style={{
            ...cardStyle,
            padding: '18px 20px',
            color: '#b91c1c',
            background: '#fef2f2',
            borderColor: '#fecaca'
          }}
        >
          {error}
        </div>
      </div>
    )
  }

  const clickers = data?.clickers || []

  return (
    <div className='font18'>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '20px'
        }}
      >
        <StatCard
          title='Total Banner Clicks'
          value={formatNumber(data?.totalClicks)}
          subtitle='All-time clicks on the Maxify IP banner'
        />
        <StatCard
          title='Unique Users Clicked'
          value={formatNumber(data?.uniqueClickers)}
          subtitle='Distinct users who clicked at least once'
        />
      </div>

      <div style={{ ...cardStyle, padding: '20px' }}>
        <h4
          style={{
            margin: '0 0 16px',
            color: '#0f172a',
            fontSize: '18px',
            fontWeight: 700
          }}
        >
          Users Who Clicked
        </h4>
        {clickers.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}
            >
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['User', 'Type', 'Clicks', 'First Clicked', 'Last Clicked'].map(
                    (heading, index) => (
                      <th
                        key={heading}
                        style={{
                          padding: '12px 16px',
                          textAlign: index === 2 ? 'right' : 'left',
                          borderBottom: '1px solid #e2e8f0',
                          fontWeight: 600,
                          color: '#0f172a',
                          fontSize: '13px'
                        }}
                      >
                        {heading}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {clickers.map((user, index) => (
                  <tr
                    key={user._id || index}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      background: index % 2 === 0 ? '#ffffff' : '#f8fafc'
                    }}
                  >
                    <td style={{ padding: '12px 16px', color: '#0f172a' }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>
                        {user.name ||
                          `${user.firstname || ''} ${user.lastname || ''}`.trim() ||
                          '--'}
                      </p>
                      <p
                        style={{
                          margin: '2px 0 0',
                          color: '#64748b',
                          fontSize: '12px'
                        }}
                      >
                        {user.email || '--'}
                      </p>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#334155' }}>
                      {user.type || '--'}
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        color: '#0f172a',
                        fontWeight: 600
                      }}
                    >
                      {formatNumber(user.maxifyBannerClick?.count)}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#334155' }}>
                      {formatDate(user.maxifyBannerClick?.firstClickedAt)}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#334155' }}>
                      {formatDate(user.maxifyBannerClick?.lastClickedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
            No one has clicked the Maxify banner yet.
          </p>
        )}
      </div>
    </div>
  )
}

export default MaxifyBannerActivity
